<template>
  <div class="purchase-quantity-selector">
    <!-- Hidden element to ensure Vue tracks availableSeatsBySection for reactivity -->
    <div style="display: none">{{ availableSeatsBySection }}</div>
    <!-- Seated Event: Show Seat Map -->
    <div v-if="purchase?.eventDate?.isSeated">
      <v-row v-if="isSjSeatMap">
        <v-col :style="{ display: isPublic ? 'block' : 'none' }">
          <purchase-quantity-selector-seat-map-panels
            v-model:expandedSeating="expandedSeating"
            v-model:expandedReservedSeatingSection="
              expandedReservedSeatingSection
            "
            v-model:expandedGASection="expandedGASection"
            :data="seatMapData"
            :state="seatMapState"
            :handlers="seatMapHandlers"
            :isSjSeatMap="isSjSeatMap"
          />
        </v-col>
      </v-row>
      <v-row v-if="isSjSeatMap">
        <v-col>
          <div class="text-caption font-italic">
            <template v-if="isTouch()">Pinch to zoom.</template>
            <template v-else>
              Use the buttons to zoom. Click-and-drag to move around.
            </template>
            Tap on a seat to view pricing and add it to your purchase.
          </div>
          <v-card variant="outlined">
            <slot name="purchase-seat-map"></slot>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="!isSjSeatMap">
        <v-col
          xs="12"
          lg="5"
          cols="12"
          :style="{ display: isPublic ? 'block' : 'none' }"
        >
          <purchase-quantity-selector-seat-map-panels
            v-model:expandedSeating="expandedSeating"
            v-model:expandedReservedSeatingSection="
              expandedReservedSeatingSection
            "
            v-model:expandedGASection="expandedGASection"
            :data="seatMapData"
            :state="seatMapState"
            :handlers="seatMapHandlers"
            :isSjSeatMap="isSjSeatMap"
          />
        </v-col>
        <v-col xs="12" :lg="isPublic ? 7 : 12" cols="12">
          <div class="text-caption font-italic">
            <template v-if="isTouch()">Pinch to zoom.</template>
            <template v-else>
              Use the buttons to zoom. Click-and-drag to move around.
            </template>
            Tap on a seat to view pricing and add it to your purchase.
          </div>
          <v-card variant="outlined">
            <slot name="purchase-seat-map"></slot>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <!-- Unseated Event: Show Table or Mobile Cards -->
    <template v-else>
      <v-card>
        <v-expansion-panels v-model="expandedPanels">
          <span v-if="!gaEventPricesExcludingComp.length">
            <p class="text-center text-caption">
              No general admission tickets available.
            </p>
          </span>
          <!-- General Admission Panel -->
          <v-expansion-panel
            v-if="gaEventPricesExcludingComp.length"
            value="general"
          >
            <v-expansion-panel-title>
              <v-icon class="mr-4">fal fa-ticket</v-icon>
              <h1>GENERAL ADMISSION</h1>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0">
              <v-table v-if="display.mdAndUp.value" dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Type</th>
                      <th class="text-center">Quantity</th>
                      <th></th>
                      <!--update/cancel buttons-->
                      <th class="text-right">
                        {{ hideServiceFee ? " " : "Price" }}
                      </th>
                      <th class="text-right">Service Fee</th>
                      <th class="text-right">Conv. Fee</th>
                      <th class="text-right">Tax</th>
                      <th class="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <purchase-quantity-selector-row
                      v-for="eventPrice in gaEventPricesExcludingComp"
                      :key="eventPrice.id!"
                      :eventPrice="eventPrice"
                      :purchase="purchase"
                      :priceBreakdown="getBreakdown(eventPrice)"
                      @pending-change="handleRowPendingChange"
                    />
                    <tr>
                      <td>
                        <v-fade-transition>
                          <div
                            class="text-h6 text-info py-3"
                            v-if="purchase?.payments?.length"
                          >
                            <span class="pr-2">Balance:</span>
                            <span
                              :class="{
                                'error--text': (purchase?.balance ?? -1) < 0,
                              }"
                            >
                              {{ formatCurrency(purchase.balance) }}
                            </span>
                          </div>
                        </v-fade-transition>
                      </td>
                      <td :colspan="display.mdAndUp.value ? 3 : 2">
                        <div class="text-h6 text-right py-3">
                          <span class="pr-2">Subtotal:</span>
                          {{ formatCurrency(totalPrice) }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-table>
              <div v-else>
                <!--mobile-->
                <div>
                  <purchase-quantity-selector-row
                    v-for="eventPrice in gaEventPricesExcludingComp"
                    :key="eventPrice.id!"
                    :purchase="purchase"
                    :eventPrice="eventPrice"
                    :priceBreakdown="
                      purchase.ticketPriceBreakdowns?.find(
                        (t) => t.eventPriceId === eventPrice.id,
                      )
                    "
                    @pending-change="handleRowPendingChange"
                  />
                </div>
                <v-divider class="mt-2"></v-divider>

                <!-- Total at Bottom -->
                <v-row class="border-0">
                  <v-col class="text-right">
                    <div class="total-container text-h6 pa-2">
                      <span class="pr-2 font-weight-bold">Subtotal:</span>
                      {{ formatCurrency(totalPrice) }}
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRef, watch } from "vue";
import { useDisplay } from "vuetify";
import { formatCurrency, isTouch } from "@common/utils";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import PurchaseQuantitySelectorRow from "./purchase-quantity-selector-row.vue";
import PurchaseQuantitySelectorSeatMapPanels, {
  type SeatMapData,
  type SeatMapHandlers,
  type SeatMapState,
} from "./purchase-quantity-selector-seat-map-panels.vue";
import { EventPriceDto, SeatMapSectionDto, SectionTypes } from "@/models.g";
import { eventBus, EventTypes } from "../../eventBus";
import { useTicketAvailability } from "./useTicketAvailability";

const expandedPanels = ref(["general"]);
const seatMapGeneralAdmissionEventPricesBySection = ref<
  Record<string, EventPriceDto[]>
>({});

const display = useDisplay();
const props = defineProps<{
  purchase: TicketPurchaseDtoViewModel;
  isPublic?: boolean;
  isSjSeatMap?: boolean;
}>();

const isPublic = computed(() => props.isPublic ?? false);
const generalAdmissionEventPrices = ref<EventPriceDto[]>([]);
const gaEventPricesExcludingComp = computed(() => {
  return generalAdmissionEventPrices.value.filter((p) => !p.isComp);
});
const highlightedPriceId = ref<number | null>(null);
const highlightedSectionName = ref<string | null>(null);

const emit = defineEmits<{
  "price-selected": [eventPrice: EventPriceDto, sectionName?: string];
  "price-cleared": [];
}>();
const localQuantity = ref<Record<number, number>>({});
const expandedSeating = ref<string | null>(null);
const expandedReservedSeatingSection = ref<string | null>(null);
const expandedGASection = ref<string | null>(null);
const seatMapHasOnlyOneSection = ref<boolean>(false);
const onlyOneReservedTicketType = ref<boolean>(false);
const onlyOneGATicketType = ref<boolean>(false);

const seatMapReservedEventPricesBySection = ref<
  Record<string, EventPriceDto[]>
>({});
const gaSectionSeatForSectionName = ref<Record<string, string>>({});
// Removed pendingEventPriceId - no longer needed with blocking logic removed

// Collect all event prices for real-time availability updates
const allEventPrices = computed(() => {
  const prices: EventPriceDto[] = [];

  // Add general admission prices
  prices.push(...generalAdmissionEventPrices.value);

  // Add seat map reserved prices
  Object.values(seatMapReservedEventPricesBySection.value).forEach(
    (sectionPrices) => {
      prices.push(...sectionPrices);
    },
  );

  // Add seat map GA prices
  Object.values(seatMapGeneralAdmissionEventPricesBySection.value).forEach(
    (sectionPrices) => {
      prices.push(...sectionPrices);
    },
  );

  return prices;
});

// Set up real-time ticket availability updates via SignalR
const eventDateId = computed(() => props.purchase.eventDate?.id);
const purchaseRef = toRef(props, "purchase");
const { availableSeatsBySection: availableSeatsBySectionRef } =
  useTicketAvailability(eventDateId, allEventPrices, purchaseRef);

// Computed property that tracks availableSeatsBySection for reactivity
const availableSeatsBySection = computed(() => {
  return (
    availableSeatsBySectionRef.value ||
    (service.getSeating.result as any)?.availableSeatsBySection ||
    (props.purchase.eventDate as any)?.availableSeatsBySection
  );
});

onMounted(() => {
  // Subscribe UnselectedSeatMapSectionClicked from eventBus
  eventBus.on(EventTypes.UnselectedSeatMapSectionClicked, expandSeating);
});

onUnmounted(() => {
  // Unsubscribe UnselectedSeatMapSectionClicked from eventBus
  eventBus.off(EventTypes.UnselectedSeatMapSectionClicked, expandSeating);
});

// This watcher ensures that when a section is highlighted (by selecting a ticket),
// the corresponding expansion panel is programmatically opened.
watch(highlightedSectionName, (newlySelectedSection) => {
  if (newlySelectedSection) {
    if (seatMapReservedEventPricesBySection.value[newlySelectedSection]) {
      expandedReservedSeatingSection.value = newlySelectedSection;
    }
    if (
      seatMapGeneralAdmissionEventPricesBySection.value[newlySelectedSection]
    ) {
      expandedGASection.value = newlySelectedSection;
    }
  }
});

function expandSeating(section: SeatMapSectionDto | null) {
  if (!section) {
    return;
  }

  expandedSeating.value =
    section.type === SectionTypes.GeneralAdmission
      ? "general-admission"
      : "reserved";

  if (section.name) {
    const sectionComponent =
      section.type === SectionTypes.GeneralAdmission
        ? expandedGASection
        : expandedReservedSeatingSection;
    sectionComponent.value = section.name;
  }
}

//watch for if it changes in the shopping cart...
watch(
  () => props.purchase.tickets,
  (tickets) => {
    const map: Record<number, number> = {};
    tickets?.forEach((t) => {
      map[t.eventPriceId!] = (map[t.eventPriceId!] || 0) + 1;
    });
    localQuantity.value = map;
  },
  { immediate: true },
);

function handleRowPendingChange() {
  // No longer needed - removed blocking logic
}

function handleSelectPrice(eventPrice: EventPriceDto, sectionName?: string) {
  // Prevent selection if another row is already selected
  if (
    highlightedPriceId.value !== null &&
    highlightedSectionName.value !== null &&
    (highlightedPriceId.value !== eventPrice.id ||
      highlightedSectionName.value !== sectionName)
  ) {
    return; // Block selection when another row is active
  }

  if (
    highlightedPriceId.value === eventPrice.id &&
    highlightedSectionName.value === sectionName
  ) {
    highlightedPriceId.value = null;
    highlightedSectionName.value = null;
    emit("price-cleared");
  } else {
    highlightedPriceId.value = eventPrice.id;
    highlightedSectionName.value = sectionName ?? null;
    emit("price-selected", eventPrice, sectionName);
  }
}

function handleGAQtyFocus(eventPrice: EventPriceDto, sectionName: string) {
  // Prevent focus/selection if another row is already selected
  if (
    highlightedPriceId.value !== null &&
    highlightedSectionName.value !== null &&
    (highlightedPriceId.value !== eventPrice.id ||
      highlightedSectionName.value !== sectionName)
  ) {
    return; // Block selection when another row is active
  }

  if (
    highlightedPriceId.value !== eventPrice.id ||
    highlightedSectionName.value !== sectionName
  ) {
    highlightedPriceId.value = eventPrice.id;
    highlightedSectionName.value = sectionName;
    emit("price-selected", eventPrice, sectionName);
  }
}

const service = new PublicPurchaseServiceViewModel();
if (props.purchase.eventDate?.isSeated) {
  service.getSeating(props.purchase.publicId);
} else {
  generalAdmissionEventPrices.value = props.purchase.eventDate?.eventPrices!;
}

// Watch for changes to eventPrices (e.g., when isSoldOut changes after API calls)
watch(
  () => props.purchase.eventDate?.eventPrices,
  (newEventPrices) => {
    if (!props.purchase.eventDate?.isSeated && newEventPrices) {
      generalAdmissionEventPrices.value = newEventPrices;
    }
  },
  { deep: true },
);

function sortEventPricesByOrder(prices: EventPriceDto[]): EventPriceDto[] {
  return prices.sort((a, b) => {
    const aOrder = a.order ?? 0;
    const bOrder = b.order ?? 0;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    // Fallback to displayName comparison when order is the same
    const aDisplayName = a.displayName ?? "";
    const bDisplayName = b.displayName ?? "";
    return aDisplayName.localeCompare(bDisplayName);
  });
}

service.getSeating.onFulfilled(async (res) => {
  if (res.wasSuccessful) {
    // Populate section availability from the raw API response before Coalesce
    // strips it — Dictionary<long, int> is typed as "unknown" in metadata.g.ts
    // so mapToModel discards it. Reading from res.result directly preserves it.
    const rawSeatsBySection = (res.result as any)?.availableSeatsBySection;
    if (
      rawSeatsBySection &&
      typeof rawSeatsBySection === "object" &&
      !Array.isArray(rawSeatsBySection)
    ) {
      availableSeatsBySectionRef.value = rawSeatsBySection;
    }

    // Not showing complimentary tickets at this moment
    const nonCompPrices = (prices: EventPriceDto[] | null) =>
      prices?.filter((p) => !p.isComp);

    const grouped: Record<string, EventPriceDto[]> = {};
    (res.result!.seatMap?.sections ?? [])
      .filter(
        (section) =>
          section.type === SectionTypes.IndividualSeat && section.prices,
      )
      .forEach((section) => {
        const prices = nonCompPrices(section.prices);
        if (section.name && prices && prices.length > 0) {
          grouped[section.name] = sortEventPricesByOrder(prices);
        }
      });
    seatMapReservedEventPricesBySection.value = grouped;

    // Group general admission by section name
    const gaGrouped: Record<string, EventPriceDto[]> = {};
    const gaSeatMap: Record<string, string> = {};
    (res.result!.seatMap?.sections ?? [])
      .filter(
        (section) =>
          section.type === SectionTypes.GeneralAdmission &&
          nonCompPrices(section.prices),
      )
      .forEach((section) => {
        const prices = nonCompPrices(section.prices);
        if (section.name && prices && prices.length > 0) {
          gaGrouped[section.name] = sortEventPricesByOrder(prices);
          gaSeatMap[section.name] = section.seats![0].seatNumber!;
        }
      });
    seatMapGeneralAdmissionEventPricesBySection.value = gaGrouped;
    gaSectionSeatForSectionName.value = gaSeatMap;
  }

  // If seat map has only one section, expand it by default
  seatMapHasOnlyOneSection.value = false;
  if (res.result?.seatMap?.sections?.length == 1) {
    expandSeating(res.result?.seatMap?.sections[0]);
    seatMapHasOnlyOneSection.value = true;
  }

  onlyOneReservedTicketType.value =
    seatMapReservedEventPricesBySection.value &&
    Object.keys(seatMapReservedEventPricesBySection.value).length == 1;

  // if there is only one reserved ticket type, expand it by default
  if (onlyOneReservedTicketType.value) {
    expandedReservedSeatingSection.value = Object.keys(
      seatMapReservedEventPricesBySection.value,
    )[0];
  }

  onlyOneGATicketType.value =
    seatMapGeneralAdmissionEventPricesBySection.value &&
    Object.keys(seatMapGeneralAdmissionEventPricesBySection.value).length == 1;

  // if there is only one general admission ticket type, expand it by default
  if (onlyOneGATicketType.value) {
    expandedGASection.value = Object.keys(
      seatMapGeneralAdmissionEventPricesBySection.value,
    )[0];
  }
});

const totalPrice = computed(() => {
  return props.purchase.grandTotal ?? 0;
});

const getBreakdown = (eventPrice: EventPriceDto) => {
  return props.purchase.ticketPriceBreakdowns?.find(
    (t) => t.eventPriceId === eventPrice.id,
  );
};

const hideServiceFee = computed(() => {
  return (props.purchase.isPublic && props.purchase.hideServiceFee)!;
});

const seatMapData = computed<SeatMapData>(() => ({
  reservedBySection: seatMapReservedEventPricesBySection.value,
  generalAdmissionBySection: seatMapGeneralAdmissionEventPricesBySection.value,
  gaSectionSeats: gaSectionSeatForSectionName.value,
  purchase: props.purchase,
}));

const seatMapState = computed<SeatMapState>(() => ({
  highlightedSectionName: highlightedSectionName.value,
  highlightedPriceId: highlightedPriceId.value,
  isPublic: isPublic.value,
  seatMapHasOnlyOneSection: seatMapHasOnlyOneSection.value,
}));

const seatMapHandlers: SeatMapHandlers = {
  getAvailableCount,
  handleSelectPrice,
  handleGAQtyFocus,
};

function getAvailableCount(eventPrice: EventPriceDto): number | null {
  // Use price-specific availableTickets if set (when MaxQuantity is configured),
  // otherwise fall back to event date's overall availableSeats for general admission events
  if (
    eventPrice.availableTickets !== undefined &&
    eventPrice.availableTickets !== null
  ) {
    return eventPrice.availableTickets;
  }

  const sectionId = (eventPrice as any).seatMapSectionId;
  // Use computed property instead of direct prop access
  const sectionData = availableSeatsBySection.value;

  if (sectionId && sectionData) {
    const available = sectionData[sectionId];
    if (available !== undefined && available !== null) {
      return available;
    }
  }

  return props.purchase.eventDate?.availableSeats ?? null;
}
</script>

<style scoped>
.mobile-price-item {
  width: 100%;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.mobile-price-item:last-child {
  border-bottom: none;
}

.quantity-selector-sidebar {
  width: 500px;
  max-width: 500px;
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .v-expansion-panel-title {
    font-size: inherit !important;
  }

  .v-expansion-panel {
    .v-expansion-panel-text {
      margin: 0px -10px -5px -10px;
    }
  }
}
</style>
